import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AmountInput from '../../../components/Utils/AmountInput';
import { apiService } from '../../../components/Config/api.service';
import { notify } from '../../../components/Utils/Notifications';
import { FormField } from '../../../components/Formulaire/FormField';
import FormSection from '../../../components/Formulaire/FormSection';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseDateAsUtc = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(Date.UTC(year, month - 1, day));
};

const computeWorkedDays = (dateStart, dateEnd) => {
  if (!dateStart || !dateEnd) return '';

  const startDate = parseDateAsUtc(dateStart);
  const endDate = parseDateAsUtc(dateEnd);

  if (!startDate || !endDate) return '';
  if (endDate < startDate) return null;

  return Math.floor((endDate.getTime() - startDate.getTime()) / MS_PER_DAY);
};

const ExternalContractForm = ({ employee = {}, currentExternal = null, onSuccess = () => {}, onCancel = () => {} }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyWorkAccident, setCompanyWorkAccident] = useState(1.0);
  const [companySettings, setCompanySettings] = useState({
    apply_external_taxes: true,
    apply_external_work_accident: false,
  });
  const [form, setForm] = useState({
    name: currentExternal?.name || '',
    type: currentExternal?.type || 'stage',
    worked_days: currentExternal?.worked_days ?? '',
    date_start: currentExternal?.date_start || '',
    date_end: currentExternal?.date_end || '',
    salary: currentExternal?.salary ?? 0,
    net_salary: currentExternal?.net_salary ?? 0,
    apply_taxes: currentExternal?.apply_taxes ?? true,
    taxes: currentExternal?.taxes ?? 5.0,
    apply_work_accident: currentExternal?.apply_work_accident ?? false,
    work_accident_rate: currentExternal?.work_accident_rate ?? 0.0,
    state: currentExternal?.state || 'active',
    employee_id: currentExternal?.employee_id || employee?.id || null,
  });

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const res = await apiService.getElement('/company/info');
        const data = res.data || {};
        if (data.work_accident !== undefined) setCompanyWorkAccident(data.work_accident);
        setCompanySettings({
          apply_external_taxes: data.apply_external_taxes ?? true,
          apply_external_work_accident: data.apply_external_work_accident ?? false,
        });
        if (!currentExternal?.id) {
          setForm(prev => ({
            ...prev,
            apply_taxes: data.apply_external_taxes ?? true,
            apply_work_accident: data.apply_external_work_accident ?? false,
          }));
        }
      } catch (err) {
        console.error('Error fetching company info:', err);
      }
    };
    fetchCompanyInfo();
  }, [currentExternal]);

  useEffect(() => {
    const fetchCurrentExternal = async () => {
      if (currentExternal?.id) {
        try {
          setLoading(true);
          const res = await apiService.getElementById('/external_contract', currentExternal.id);
          const data = res.data || {};
          setForm({
            name: data.name || '',
            type: data.type || 'stage',
            worked_days: data.worked_days ?? '',
            date_start: data.date_start || '',
            date_end: data.date_end || '',
            salary: data.salary ?? 0,
            net_salary: data.net_salary ?? 0,
            apply_taxes: data.apply_taxes ?? true,
            taxes: data.taxes ?? 5.0,
            apply_work_accident: data.apply_work_accident ?? false,
            work_accident_rate: data.work_accident_rate ?? 0.0,
            state: data.state || 'draft',
            employee_id: data.employee_id || employee?.id || null,
          });
          setError('');
        } catch (err) {
          console.error('Error fetching external contract:', err);
          setError(t('externalContractForm.fetchError'));
        } finally {
          setLoading(false);
        }
      } else {
        setForm(prev => ({ ...prev, employee_id: employee?.id || null }));
      }
    };
    fetchCurrentExternal();
  }, [currentExternal, employee]);

  useEffect(() => {
    const nextWorkedDays = computeWorkedDays(form.date_start, form.date_end);

    if (nextWorkedDays === null) {
      setError(t('externalContractForm.endDateError'));
      setForm(prev => (prev.worked_days === '' ? prev : { ...prev, worked_days: '' }));
      return;
    }

    setError('');
    setForm(prev => (prev.worked_days === nextWorkedDays ? prev : { ...prev, worked_days: nextWorkedDays }));
  }, [form.date_start, form.date_end]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNetChange = (newValue) => {
    setForm(prev => ({ ...prev, net_salary: Number(newValue) || 0 }));
  };

  const handleTaxesChange = (e) => {
    const v = parseFloat(e.target.value);
    setForm(prev => ({ ...prev, taxes: isNaN(v) ? 0 : v }));
  };

  const computeSalary = () => {
    const net_salary = Number(form.net_salary || 0);
    const tax_rate = form.apply_taxes ? (Number(form.taxes || 0) / 100) : 0;
    const at_rate = (form.type === 'stage' && form.apply_work_accident)
      ? (Number(companyWorkAccident || form.work_accident_rate || 0) / 100)
      : 0;

    if ((tax_rate + at_rate) >= 1 || (1 - tax_rate) <= 0) return 0;

    let gross = net_salary / (1 - tax_rate - at_rate);
    if (gross > 63000 && at_rate > 0) {
      const at_amount = 63000 * at_rate;
      gross = (net_salary + at_amount) / (1 - tax_rate);
    }
    return isFinite(gross) ? Math.round(gross) : 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (computeWorkedDays(form.date_start, form.date_end) === null) {
      setError(t('externalContractForm.endDateError'));
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: form.name,
        type: form.type,
        worked_days: Number(form.worked_days || 0),
        date_start: form.date_start || null,
        date_end: form.date_end || null,
        net_salary: Number(form.net_salary || 0),
        apply_taxes: Boolean(form.apply_taxes),
        taxes: Number(form.taxes || 0),
        apply_work_accident: form.type === 'stage' ? Boolean(form.apply_work_accident) : false,
        state: form.state,
        employee: form.employee_id || employee?.id,
      };

      if (currentExternal?.id) {
        await apiService.updateElementById('/external_contract', currentExternal.id, payload);
        notify(t('externalContractForm.updated'), '', 2);
        setSuccess(t('externalContractForm.updated'));
      } else {
        await apiService.saveElement('/external_contract', payload);
        notify(t('externalContractForm.created'), '', 2);
        setSuccess(t('externalContractForm.created'));
      }

      setTimeout(() => onSuccess(), 300);
    } catch (err) {
      console.error(err);
      setError(t('externalContractForm.genericError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      <FormSection title={t('externalContractForm.sectionInfo')} color="blue">
        <FormField label={t('externalContractForm.contractName')}>
          <input
            name="name"
            value={form.name || ''}
            onChange={handleChange}
            required
            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
          />
        </FormField>

        <FormField label={t('externalContractForm.status')}>
          <select name="state" value={form.state} onChange={handleChange} className="select select-bordered w-full">
            <option value="draft">{t('externalContractForm.statusDraft')}</option>
            <option value="active">{t('externalContractForm.statusActive')}</option>
            <option value="expired">{t('externalContractForm.statusExpired')}</option>
            <option value="cancel">{t('externalContractForm.statusCancelled')}</option>
          </select>
        </FormField>

        <FormField label={t('externalContractForm.employee')}>
          <input type="text" value={employee?.name || ''} readOnly className="input input-bordered w-full" />
        </FormField>

        <FormField label={t('externalContractForm.type')}>
          <select name="type" value={form.type} onChange={handleChange} className="select select-bordered w-full">
            <option value="stage">{t('externalContractForm.typeIntern')}</option>
            <option value="prestation">{t('externalContractForm.typeContractor')}</option>
          </select>
        </FormField>
      </FormSection>

      <FormSection title={t('externalContractForm.sectionPeriod')} color="orange">
        <FormField label={t('externalContractForm.startDate')}>
          <input type="date" name="date_start" value={form.date_start || ''} onChange={handleChange} className="input input-bordered w-full" />
        </FormField>
        <FormField label={t('externalContractForm.endDate')}>
          <input type="date" name="date_end" value={form.date_end || ''} onChange={handleChange} min={form.date_start || undefined} className="input input-bordered w-full" />
        </FormField>
        <FormField label={t('externalContractForm.workedDays')}>
          <input
            type="number"
            name="worked_days"
            value={form.worked_days}
            onChange={handleChange}
            className="input input-bordered w-full"
            readOnly
          />
        </FormField>
      </FormSection>

      <FormSection title={t('externalContractForm.sectionSalary')} color="purple">
        <FormField label={t('externalContractForm.netSalary')}>
          <AmountInput value={form.net_salary} onChange={handleNetChange} className="w-full input input-bordered focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20" />
        </FormField>

        {/* Configuration Taxe et AT (Définis globalement sur la société) */}
        <div className="col-span-2 flex flex-col gap-3 border-t border-b border-gray-100 py-4 my-2 bg-gray-50/50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Configuration globale Société</span>
            <span className="text-xs text-blue-600 font-medium">Paramétrable dans Paramètres &gt; Société</span>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="apply_taxes"
              name="apply_taxes"
              checked={form.apply_taxes}
              onChange={handleChange}
              className="checkbox checkbox-primary checkbox-sm"
            />
            <label htmlFor="apply_taxes" className="text-sm font-medium text-gray-700 cursor-pointer">
              Appliquer la taxe / retenue (Configuration Société)
            </label>
          </div>

          {form.apply_taxes && (
            <FormField label={t('externalContractForm.taxes')}>
              <input
                type="number"
                step="0.1"
                min="0"
                name="taxes"
                value={form.taxes}
                onChange={handleTaxesChange}
                className="input input-bordered w-full"
              />
            </FormField>
          )}

          {form.type === 'stage' && (
            <div className="flex flex-col gap-1 pt-1">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="apply_work_accident"
                  name="apply_work_accident"
                  checked={form.apply_work_accident}
                  onChange={handleChange}
                  className="checkbox checkbox-primary checkbox-sm"
                />
                <label htmlFor="apply_work_accident" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Appliquer l'Accident du Travail (AT) pour ce stagiaire
                </label>
              </div>
              <span className="text-xs text-gray-500 ml-7">
                Taux entreprise : {companyWorkAccident}% (Base plafonnée à 63 000 FCFA)
              </span>
            </div>
          )}
        </div>

        {/* Affichage formaté */}
        <div className="w-full flex gap-4 col-span-2 items-center pt-2">
          <span>{t('externalContractForm.computedGrossSalary')} :</span>
          <span className='text-[--color-primary] font-bold text-lg'>
            {new Intl.NumberFormat(dateLocale, { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(computeSalary())}
          </span>
        </div>
      </FormSection>

      <div className="flex justify-center mt-8 gap-4">
        <button type="submit" disabled={loading} className="bg-[--color-primary] hover:bg-[--bg-hover] rounded-full text-white px-12 py-3 font-medium transition-all duration-200 shadow-sm">
          {loading ? (currentExternal ? t('externalContractForm.processing') : t('externalContractForm.creating')) : (currentExternal ? t('externalContractForm.update') : t('externalContractForm.create'))}
        </button>
      </div>
    </form>
  );
};

export default ExternalContractForm;